import { useMemo } from "react";
import { cx, formatPhone } from "shared/utils";
import {
  Download04Icon,
  AlertCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { MessageContentCard } from "./MessageContentCard";
import {
  MessageAddressField,
  type MessageAddress,
  type MessageAddressVariant
} from "./MessageAddressField";
import { useInboxEmbed } from "../context/useInboxEmbed";
import type { Attachment, SmsKeywordType } from "../types";
import { isImageAttachment } from "../utils/attachment";

type SmsAddress = MessageAddress;

// Design 2152:20753: 11px From/To block; the floating card stacks the name
// over the number, the drawer keeps one truncated line.
const addressVariantFor = (
  compact: boolean,
  layout?: "floating" | "drawer"
): MessageAddressVariant => {
  if (!compact) return "default";
  return layout === "drawer" ? "compact-inline" : "compact-stacked";
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface SmsContentProps {
  isFromContact: boolean;
  body: string;
  from?: SmsAddress;
  to?: SmsAddress;
  attachments?: Attachment[];
  smsKeywordType?: SmsKeywordType | null;
  className?: string;
}

export const SmsContent = ({
  isFromContact,
  body,
  from,
  to,
  attachments,
  smsKeywordType,
  className
}: SmsContentProps) => {
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const addressVariant = addressVariantFor(isCompact, embed?.layout);
  // Only opt-out has a notice per design — opt-in (START) doesn't show one.
  const smsKeywordNotice =
    smsKeywordType === "opt_out" && from?.address
      ? `Phone number ${formatPhone(from.address)} has opted out of SMS communications`
      : null;
  const imageAttachments = useMemo(
    () =>
      attachments?.filter((att) => att.file_url && isImageAttachment(att)) ??
      [],
    [attachments]
  );

  const fileAttachments = useMemo(
    () =>
      attachments?.filter((att) => att.file_url && !isImageAttachment(att)) ??
      [],
    [attachments]
  );

  return (
    <MessageContentCard
      isFromContact={isFromContact}
      className={cx("flex flex-col divide-y divide-alpha-40", className)}
    >
      <div
        className={cx(
          isCompact
            ? "space-y-1 pb-3 text-[11px] leading-4"
            : "space-y-2 text-xs pb-4"
        )}
      >
        {from ? (
          <MessageAddressField
            label="From:"
            address={from}
            variant={addressVariant}
          />
        ) : null}
        {to ? (
          <MessageAddressField
            label="To:"
            address={to}
            variant={addressVariant}
          />
        ) : null}
      </div>

      {body ? (
        <div
          className={cx(
            "text-sm text-content-1000 break-words whitespace-pre-wrap",
            isCompact ? "pt-3" : "pt-4",
            smsKeywordNotice && "pb-4"
          )}
        >
          {body}
        </div>
      ) : null}

      {imageAttachments.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 pt-4">
          {imageAttachments.map((att) => (
            <a
              key={att.id}
              href={att.file_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={att.file_url}
                alt={att.filename}
                className="rounded-lg max-h-64 w-full object-cover"
              />
            </a>
          ))}
        </div>
      ) : null}

      {fileAttachments.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 pt-4">
          {fileAttachments.map((att) => (
            <a
              key={att.id}
              href={att.file_url}
              download={att.filename}
              className="flex items-center gap-3 p-3 rounded-xl border border-alpha-100 hover:bg-background-800 transition-colors"
            >
              <HugeiconsIcon
                icon={Download04Icon}
                size={24}
                className="text-content-300 flex-shrink-0"
              />
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-content-1000 truncate">
                  {att.filename}
                </span>
                {att.file_size_bytes ? (
                  <span className="text-xs text-content-200">
                    {formatFileSize(att.file_size_bytes)}
                  </span>
                ) : null}
              </div>
            </a>
          ))}
        </div>
      ) : null}

      {smsKeywordNotice ? (
        <div className="flex items-center gap-2 pt-4">
          <HugeiconsIcon
            icon={AlertCircleIcon}
            size={20}
            className="text-content-1000 flex-shrink-0"
          />
          <span className="text-base font-medium italic text-content-1000">
            {smsKeywordNotice}
          </span>
        </div>
      ) : null}
    </MessageContentCard>
  );
};
